import { useEffect, useState } from 'react';

/**
 * Phone-sized viewport, kept current as the window changes.
 *
 * 768px is Tailwind's `md`, and the line is drawn there on purpose: a portrait iPad is exactly
 * 768 and counts as a tablet, so tablets get desktop behaviour and only phones fall below.
 *
 * <p>It answers a viewport question, not a device one - a narrow window on a desktop reads as a
 * phone here. That is the right trade for what it is used for (whether a link opens in a new tab):
 * being wrong costs a tab, and viewport width is what actually predicts whether a second tab is
 * usable.
 *
 * <p>Guarded for the no-matchMedia case (SSR, very old browsers) by defaulting to false, so the
 * desktop behaviour is the fallback rather than a crash.
 */
export default function useIsPhone() {
  const query = '(max-width: 767px)';
  const [isPhone, setIsPhone] = useState(() => {
    try {
      return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        && window.matchMedia(query).matches;
    } catch (_) {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    let mql;
    try {
      mql = window.matchMedia(query);
    } catch (_) {
      return undefined;
    }
    const onChange = (e) => setIsPhone(e.matches);
    setIsPhone(mql.matches);
    // addListener is the pre-Safari-14 spelling; without it this silently never updates there.
    if (mql.addEventListener) mql.addEventListener('change', onChange);
    else if (mql.addListener) mql.addListener(onChange);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener('change', onChange);
      else if (mql.removeListener) mql.removeListener(onChange);
    };
  }, []);

  return isPhone;
}
